/**
 * Composite-task prompt propagation rule.
 *
 * A "Task" buckets one or more LLMUsecaseInfo rows under a single
 * ``(group_name, sub_group_name)`` pair. Composite (multi-objective) tasks
 * have multiple subtask usecases sharing one ``sub_group_name``. The Model
 * and Prompt Setup modal is opened for one subtask at a time, but for
 * unconfigured subtasks the user obviously means "use this for the whole
 * task" — so the first save fans out to every empty sibling. Once a sibling
 * has a prompt it drops out of the propagation set, so subsequent edits
 * only touch the subtask the user is currently looking at.
 *
 * This module is the pure decision layer used by ``TaskPromptSetupModal``.
 */

/**
 * A subtask is considered "configured" once it has any non-blank prompt.
 * Teacher model alone is not enough — the Model and Prompt Setup modal
 * requires a prompt to save, so prompt presence is the canonical signal
 * that the user has touched this subtask.
 *
 * @param {Object} usecase LLMUsecaseInfo row (or anything with `.prompt`).
 * @returns {boolean}
 */
export const hasConfiguredPrompt = (usecase) =>
    Boolean(usecase?.prompt && String(usecase.prompt).trim());

/**
 * Composed-task header rules need a broader signal than `hasConfiguredPrompt`:
 * if the user has picked either a prompt OR a teacher model on any subtask,
 * we treat the composed task as "touched".
 *
 * @param {Object} usecase LLMUsecaseInfo row.
 * @returns {boolean}
 */
export const hasAnyTaskConfig = (usecase) => {
    if (!usecase) return false;
    if (hasConfiguredPrompt(usecase)) return true;
    const teacherModel = usecase.teacher_model;
    return Boolean(teacherModel && String(teacherModel).trim());
};

/**
 * `true` when none of the supplied subtasks has a prompt or teacher model.
 * Used by composed-task header / shared-config helpers to detect a blank task.
 *
 * @param {Array<Object>} subGroupUsecases All sub-task usecases under the
 *   active task's `sub_group_name`.
 * @returns {boolean}
 */
export const noSubtaskConfigured = (subGroupUsecases) => {
    if (!Array.isArray(subGroupUsecases) || subGroupUsecases.length === 0) {
        return true;
    }
    return subGroupUsecases.every((u) => !hasAnyTaskConfig(u));
};

/**
 * Legacy helper kept for callers/tests. Always returns ``false`` — the Model
 * and Prompt Setup modal must not auto-open when navigating into a task
 * without a prompt; users open it explicitly from the task UI instead.
 *
 * @param {Array<Object>} _subGroupUsecases
 * @returns {boolean}
 */
export const shouldAutoOpenTaskPromptSetupFromProjects = (_subGroupUsecases) => false;

/**
 * `true` when every subtask shares the same trimmed prompt, teacher
 * provider, and teacher model. Single-objective tasks (length 0 or 1)
 * trivially satisfy this. Used by the header to decide whether the
 * shared "Task Prompt" button should be visible.
 *
 * @param {Array<Object>} subGroupUsecases All sub-task usecases.
 * @returns {boolean}
 */
export const allSubtasksShareSameConfig = (subGroupUsecases) => {
    if (!Array.isArray(subGroupUsecases) || subGroupUsecases.length <= 1) {
        return true;
    }
    const fingerprint = (u) => {
        const prompt = (u?.prompt ?? '').toString().trim();
        const provider = (u?.teacher_provider ?? 'openai') || 'openai';
        const model = u?.teacher_model || null;
        return JSON.stringify([prompt, provider, model]);
    };
    const first = fingerprint(subGroupUsecases[0]);
    return subGroupUsecases.every((u) => fingerprint(u) === first);
};

/**
 * Pick the sibling subtasks that should receive the same model+prompt the
 * user just entered for ``currentUsecaseId``.
 *
 * Two flavours, both expressed as a single helper:
 *
 *   1. Edit mode: ``isFirstTimeSession === false``. The current subtask was
 *      already configured before this modal opened, so the user is editing
 *      one specific subtask and we never touch the siblings — return ``[]``.
 *
 *   2. First-time session: ``isFirstTimeSession === true``. The current
 *      subtask was empty when the modal opened, so the user is configuring
 *      the whole composite task. We propagate to:
 *        - siblings whose prompt is currently empty (initial fan-out), OR
 *        - siblings we already propagated to earlier in the same modal
 *          session (so subsequent debounced auto-saves continue to keep
 *          them in sync until the modal is closed).
 *
 * Rules common to both:
 *   - Skip the current usecase itself.
 *   - Skip rows with no `_id` (defensive).
 *
 * For a single-objective task this always returns ``[]``, so callers can
 * use this helper unconditionally.
 *
 * @param {Object} args
 * @param {string|undefined} args.currentUsecaseId Active subtask's `_id`.
 * @param {Array<Object>} [args.subGroupUsecases] All usecases under the
 *   active task's `sub_group_name`. Typically ``currentSubGroup.usecases``
 *   from the global state.
 * @param {boolean} [args.isFirstTimeSession=true] Whether the current
 *   modal session was opened on an unconfigured subtask. Defaults to
 *   ``true`` for backward compatibility with single-shot save callers
 *   that don't care about edit-mode gating.
 * @param {Set<string>|Array<string>} [args.alreadyPropagatedIds] Sibling
 *   ids that received an auto-saved value earlier in this same modal
 *   session. Used to keep them in sync as the user keeps typing.
 * @param {boolean} [args.forceAllSiblings=false] Override the normal
 *   first-time/empty gating and return every sibling with an `_id`. Used
 *   by the header-level "Task Prompt" button on composed tasks where the
 *   modal explicitly edits the shared config across all subtasks.
 * @returns {Array<Object>} Siblings that need propagation.
 */
export const siblingsNeedingPromptPropagation = ({
    currentUsecaseId,
    subGroupUsecases,
    isFirstTimeSession = true,
    alreadyPropagatedIds,
    forceAllSiblings = false,
}) => {
    if (!currentUsecaseId) return [];
    if (!Array.isArray(subGroupUsecases) || subGroupUsecases.length === 0) return [];

    if (forceAllSiblings) {
        return subGroupUsecases.filter(
            (sib) => sib?._id && sib._id !== currentUsecaseId
        );
    }

    if (!isFirstTimeSession) return [];

    const propagated =
        alreadyPropagatedIds instanceof Set
            ? alreadyPropagatedIds
            : new Set(Array.isArray(alreadyPropagatedIds) ? alreadyPropagatedIds : []);

    return subGroupUsecases.filter(
        (sib) =>
            sib?._id &&
            sib._id !== currentUsecaseId &&
            (!hasConfiguredPrompt(sib) || propagated.has(sib._id))
    );
};
